import { useEffect, useMemo, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  TextField, MenuItem, Stack, Alert, Typography,
} from '@mui/material';
import { slugify } from '../../utils/slugify';
import ContactSelector from '../team/ContactSelector';

// FEAT-003 FeatureCreateDialog — 신규 Feature 생성 모달.
// 결정 11 (id slugify 자동 + 중복 시 사용자 수동 수정).
// 초기 components 는 빈 배열 — 생성 후 행 ⋮ 메뉴 "구성 SW 추가" 로 채움.
//
// Feature 자체 status enum 은 components.status 와 다름 (planned/in-development/sqa/training/released/on-hold).
// 본 작업에서는 FeatureEditor 와 동일 유지.
//
// Props:
//   open               : boolean
//   onClose            : 모달 닫기
//   existingFeatureIds : 기존 Feature id Set (중복 검출용)
//   onSave             : (newFeature) => void
const FEATURE_STATUSES = ['planned', 'in-development', 'sqa', 'training', 'released', 'on-hold'];

export default function FeatureCreateDialog({ open, onClose, existingFeatureIds, members = [], onSave }) {
  const [name, setName] = useState('');
  const [id, setId] = useState('');
  const [idEdited, setIdEdited] = useState(false);
  const [owner, setOwner] = useState('');
  const [status, setStatus] = useState('planned');
  const [release, setRelease] = useState('');

  useEffect(() => {
    if (!open) return;
    setName('');
    setId('');
    setIdEdited(false);
    setOwner('');
    setStatus('planned');
    setRelease('');
  }, [open]);

  // name 입력 → id 자동 갱신 (사용자가 id 를 수동 수정한 적 없을 때만)
  const autoId = useMemo(() => slugify(name), [name]);
  useEffect(() => {
    if (!idEdited) setId(autoId);
  }, [autoId, idEdited]);

  // 중복 검증
  const idDuplicate = id && existingFeatureIds && existingFeatureIds.has(id);
  // id 형식 검증 — UPPER-KEBAB 표준 (features.yaml 컨벤션) — 대문자 / 숫자 / 하이픈 만
  const idFormatValid = /^[A-Z0-9-]+$/.test(id);
  const idError = !id
    ? '식별자 필수'
    : !idFormatValid
      ? '대문자(A-Z) · 숫자(0-9) · 하이픈(-) 만 사용 가능'
      : idDuplicate
        ? '이미 존재하는 Feature 식별자 — 다른 값으로 변경'
        : '';

  const canSave = !!name.trim() && !idError;

  const handleSave = () => {
    if (!canSave) return;
    onSave({
      id,
      name: name.trim(),
      owner: owner.trim(),
      status,
      timeline: release ? { release } : {},
      components: [],
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>새 Feature 만들기</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            size="small"
            label="이름 *"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="예: RAYTeams V4 리빌딩"
            required
            autoFocus
          />

          <TextField
            size="small"
            label="식별자 (ID)"
            value={id}
            onChange={(e) => { setIdEdited(true); setId(e.target.value); }}
            error={!!idError && !!id}
            helperText={idError || '이름 입력 시 자동 생성 — 필요 시 수동 수정 가능'}
            sx={{ fontFamily: 'JetBrains Mono, monospace' }}
          />

          <ContactSelector
            value={owner}
            onChange={(v) => setOwner(v || '')}
            members={members}
            label="담당 (PL)"
          />

          <TextField
            select
            size="small"
            label="Feature 상태"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
          >
            {FEATURE_STATUSES.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>

          <TextField
            size="small"
            label="릴리즈 예정일 (선택)"
            type="date"
            value={release}
            onChange={(e) => setRelease(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <Alert severity="info">
            <Typography variant="caption">
              구성 소프트웨어는 생성 후 매트릭스에서 행의 ⋮ 메뉴 "구성 SW 추가" 로 채울 수 있습니다.
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          생성 → buffer 에 누적
        </Button>
      </DialogActions>
    </Dialog>
  );
}
